import React, { useState, useEffect } from 'react';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { getDatabase, ref, remove, get, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import fc from '../firebaseConfig';

const db = getDatabase(fc);

const colorArray = [
  "#94651c", "#9d4730", "#853031", "#721043", "#3d2357", "#0b4453", "#2d5b55", "#606922"
];

const fetchEmployees = async () => {
  const employeesRef = ref(db, 'employees');
  const snapshot = await get(employeesRef);
  const employees = [];
  if (snapshot.exists()) {
    snapshot.forEach((childSnapshot) => {
      employees.push({ id: childSnapshot.key, ...childSnapshot.val() });
    });
  }
  return employees;
};

const deleteEmployee = async (id) => {
  const employeeRef = ref(db, `employees/${id}`);
  await remove(employeeRef);
};

const updateEmployee = async (id, updatedData) => {
  const employeeRef = ref(db, `employees/${id}`);
  await update(employeeRef, updatedData);
};

const TableEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [employeeToEdit, setEmployeeToEdit] = useState({ name: '', number: '', color: '' });
  const [showColorGrid, setShowColorGrid] = useState(false);
  const [errors, setErrors] = useState({ name: false, number: false, color: false });
  const navigate = useNavigate();

  useEffect(() => {
    const checkLoginStatus = () => {
      if (!localStorage.getItem('LOGGED_IN')) {
        navigate('/');
      }
    };

    checkLoginStatus();
    
    const getEmployees = async () => {
      const employeesList = await fetchEmployees();
      setEmployees(employeesList);
    };
    
    getEmployees();
  }, [navigate]);

  const handleDelete = async () => {
    if (employeeToDelete) {
      await deleteEmployee(employeeToDelete.id);
      setOpenDeleteDialog(false);
      const employeesList = await fetchEmployees();
      setEmployees(employeesList);
    }
  };

  const handleSaveEdit = async () => {
    const { name, number, color } = employeeToEdit;

    // Validate fields
    if (!name || !number || !color) {
      setErrors({
        name: !name,
        number: !number,
        color: !color,
      });
      return;
    }

    if (employeeToEdit.id) {
      await updateEmployee(employeeToEdit.id, {
        name: employeeToEdit.name,
        number: employeeToEdit.number,
        color: employeeToEdit.color
      });
      const employeesList = await fetchEmployees();
      setEmployees(employeesList);
      setOpenEditDialog(false);
    }
  };

  const openDeleteDialogHandler = (employee) => {
    setEmployeeToDelete(employee);
    setOpenDeleteDialog(true);
  };

  const closeDeleteDialogHandler = () => {
    setOpenDeleteDialog(false);
    setEmployeeToDelete(null);
  };

  const openEditDialogHandler = (employee) => {
    setEmployeeToEdit({ id: employee.id, name: employee.name, number: employee.number, color: employee.color });
    setOpenEditDialog(true);
    setErrors({ name: false, number: false, color: false }); // Reset errors when opening dialog
  };

  const closeEditDialogHandler = () => {
    setOpenEditDialog(false);
    setEmployeeToEdit({ name: '', number: '', color: '' });
  };

  const handleColorSelect = (selectedColor) => {
    setEmployeeToEdit(prev => ({ ...prev, color: selectedColor }));
    setShowColorGrid(false);
  };

  return (
    <div>
      <Navbar />
      <TableContainer component={Paper} style={{ maxWidth: '900px', margin: '60px auto' }}>
        <h2 style={{ marginLeft: '15px' }} className='header'>Tabela Pracowników</h2>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Imię i Nazwisko</TableCell>
              <TableCell>Numer</TableCell>
              <TableCell>Kolor</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee) => (
              <TableRow key={employee.id}>
                <TableCell>{employee.name}</TableCell>
                <TableCell>{employee.number}</TableCell>
                <TableCell>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: employee.color,
                      display: 'inline-block',
                      borderRadius: '4px',
                      border: '2px solid black'
                    }}
                  ></div>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => openEditDialogHandler(employee)}
                    style={{ marginRight: '10px' }}
                  >
                    Edytuj
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => openDeleteDialogHandler(employee)}
                  >
                    Usuń
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={closeDeleteDialogHandler}
      >
        <DialogTitle>Potwierdzenie usunięcia</DialogTitle>
        <DialogContent>
          <p>Czy na pewno chcesz usunąć {employeeToDelete?.name}?<br/>Pamiętaj, że wszystkie usługi tego pracownika zostaną usunięte</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialogHandler}>Nie</Button>
          <Button onClick={handleDelete} color="secondary">Tak</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={closeEditDialogHandler}
      >
        <DialogTitle>
          Edytuj Pracownika
          <br /><br />
          <a
            style={{
              color: '#ff0000',
              fontSize: '15px',
              lineHeight: '0.2'
            }}
          >
            UWAGA! Jeśli zmodyfikujesz pole "Imię i nazwisko",
            <br />
            usługi tego pracownika zostaną usunięte
          </a>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Imię i Nazwisko"
            fullWidth
            value={employeeToEdit.name}
            onChange={(e) => setEmployeeToEdit(prev => ({ ...prev, name: e.target.value }))}
            error={errors.name}
            helperText={errors.name ? "Pole nie może być puste" : ""}
          />
          <TextField
            margin="dense"
            label="Numer"
            fullWidth
            value={employeeToEdit.number}
            onChange={(e) => setEmployeeToEdit(prev => ({ ...prev, number: e.target.value }))}
            error={errors.number}
            helperText={errors.number ? "Pole nie może być puste" : ""}
          />
          <div style={{ marginTop: '10px' }}>
            <div
              onClick={() => setShowColorGrid(!showColorGrid)}
              style={{
                width: '100%',
                height: '2rem',
                backgroundColor: employeeToEdit.color,
                border: '2px solid black',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            ></div>
            {showColorGrid && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', marginTop: '10px', rowGap: '10px' }}>
                {colorArray.map((colorOption, index) => (
                  <div
                    key={index}
                    onClick={() => handleColorSelect(colorOption)}
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: colorOption,
                      border: '2px solid black',
                      cursor: 'pointer'
                    }}
                  ></div>
                ))}
              </div>
            )}
          </div>
          {errors.color && <p style={{ color: 'red' }}>Wybierz kolor</p>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialogHandler} color="primary">
            Anuluj
          </Button>
          <Button onClick={handleSaveEdit} color="primary">
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TableEmployees;
